import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { rememberLanguage } from '../../app/language-memory';
import { useLanguage } from '../../core/hooks/use-language';
import { useSession } from '../../core/hooks/use-session';
import { useTranslation } from '../../core/hooks/use-translation';
import { LANGUAGE_SHORT_LABELS } from '../../core/i18n/language-labels';
import type { Language } from '../../core/i18n/language';
import { BrandLogo } from './brand-logo';

/**
 * The navigation bar, which on this page sits inside the hero rather than above it — transparent,
 * in the flow, over the gradient. That is why it is here and not in `SiteHeader`: it is part of the
 * hero's composition, and lifting it out would leave a white bar on top of the gradient.
 *
 * The section links are plain anchors, not router links. They address a fragment of the page the
 * visitor is already on, so the browser's own smooth scroll (`html { scroll-behavior: smooth }`) is
 * the whole behaviour and a router navigation would only get in its way.
 */
export function HomeTopbar() {
    const { t } = useTranslation();
    const language = useLanguage();
    const navigate = useNavigate();
    const session = useSession();
    const [menuOpen, setMenuOpen] = useState(false);

    // The drawer covers the page, so the page behind it must not scroll under it.
    useEffect(() => {
        if (!menuOpen) {
            return;
        }

        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const close = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMenuOpen(false);
            }
        };

        document.addEventListener('keydown', close);

        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener('keydown', close);
        };
    }, [menuOpen]);

    const switchTo = (next: Language) => {
        rememberLanguage(next);
        void navigate(`/${next}`);
    };

    return (
        <header className="hero-topbar">
            <div className="header-inner">
                <a href="#home" className="logo">
                    <BrandLogo />
                </a>

                <nav className={menuOpen ? 'nav nav-open' : 'nav'}>
                    <ul className="nav-links" onClick={() => setMenuOpen(false)}>
                        <li>
                            <a href="#home" className="nav-active">
                                {t('site.nav.home')}
                            </a>
                        </li>
                        <li>
                            <a href="#about">{t('site.nav.about')}</a>
                        </li>
                        <li>
                            <a href="#mortgage-programs">{t('site.nav.programs')}</a>
                        </li>
                        <li>
                            <a href="#faq">{t('site.nav.faq')}</a>
                        </li>
                        <li>
                            <a href="#contact">{t('site.nav.contact')}</a>
                        </li>
                    </ul>

                    {/*
                        The way in to the portal, and the only element on this page that changes
                        with who is reading it. It carries `btn` so the drawer's existing
                        show-and-hide rules cover it — `.nav .btn` is what the mobile breakpoint
                        addresses — and `nav-account` takes the border and the fill back off, so the
                        hero keeps one bordered button and this reads as secondary to it rather than
                        competing with the booking call to action beside it.
                    */}
                    <Link
                        to={session === null ? `/${language}/account/sign-in` : `/${language}/portal`}
                        className="btn btn-sm nav-account"
                        onClick={() => setMenuOpen(false)}
                    >
                        <AccountIcon />
                        {session === null ? t('nav.sign_in') : t('nav.portal')}
                    </Link>

                    <Link
                        to={`/${language}/book`}
                        className="btn btn-primary btn-sm"
                        onClick={() => setMenuOpen(false)}
                    >
                        {t('site.nav.cta')}
                    </Link>
                </nav>

                <div className="lang-switch" role="group" aria-label={t('site.nav.language')}>
                    <button
                        type="button"
                        className={language === 'kk' ? 'active' : undefined}
                        onClick={() => switchTo('kk')}
                    >
                        {LANGUAGE_SHORT_LABELS.kk}
                    </button>
                    <button
                        type="button"
                        className={language === 'ru' ? 'active' : undefined}
                        onClick={() => switchTo('ru')}
                    >
                        {LANGUAGE_SHORT_LABELS.ru}
                    </button>
                </div>

                <button
                    type="button"
                    className={menuOpen ? 'hamburger active' : 'hamburger'}
                    aria-label={t('site.nav.menu')}
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((open) => !open)}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </div>

            {/*
                The scrim goes to `document.body`, as it does on the live site. The drawer is
                `position: fixed`, and both of them sit above the page at z-indexes the hero must
                not enclose — rendering the scrim in here would put it inside the hero's own
                stacking order and let it paint over the drawer it is meant to sit behind.
            */}
            {menuOpen &&
                createPortal(
                    <div className="nav-overlay active" onClick={() => setMenuOpen(false)} />,
                    document.body
                )}
        </header>
    );
}

/** `aria-hidden` because it sits beside its own label; announcing it would say the word twice. */
function AccountIcon() {
    return (
        <svg
            className="nav-account-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );
}
